import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Tag, 
  Space, 
  Input, 
  Select, 
  Tabs, 
  message,
  Modal,
  Typography
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { AudioAsset } from '../../types';
import { UploadAudioModal } from './UploadAudioModal';
import { EditAudioModal } from './EditAudioModal';

const { TabPane } = Tabs;
const { Option } = Select;

export const AudioLibrary: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'music' | 'sound_effect'>('all');
  const [data, setData] = useState<AudioAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt_desc');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<AudioAsset | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageSize,
        pageIndex,
        sortBy
      };
      
      if (activeTab !== 'all') {
        params.type = activeTab;
      }
      
      if (search) {
        params.search = search;
      }

      const result = await audioLibraryApi.findMany(params);
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error: any) {
      message.error('Failed to load audio assets: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab, pageIndex, pageSize, search, sortBy]);

  const handlePlayPause = (audio: AudioAsset) => {
    if (playingAudioId === audio.id && audioElement && !audioElement.paused) {
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      if (audioElement) {
        audioElement.pause();
      }
      const newAudio = new Audio(audio.url);
      newAudio.play();
      setAudioElement(newAudio);
      setPlayingAudioId(audio.id);
      
      newAudio.onended = () => {
        setPlayingAudioId(null);
      };
    }
  };

  const handleEdit = (audio: AudioAsset) => {
    setSelectedAudio(audio);
    setEditModalVisible(true);
  };

  const handleDelete = (audio: AudioAsset) => {
    Modal.confirm({
      title: 'Archive Audio',
      content: `Are you sure you want to archive "${audio.name}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await audioLibraryApi.deleteAudio(audio.id);
          message.success('Audio archived successfully');
          loadData();
        } catch (error: any) {
          message.error('Failed to archive audio: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleAnalyze = async (audio: AudioAsset) => {
    try {
      await audioLibraryApi.analyzeAudio(audio.id);
      message.success('AI analysis started. This may take a few moments...');
      setTimeout(() => loadData(), 2000);
    } catch (error: any) {
      message.error('Failed to start AI analysis: ' + (error.message || 'Unknown error'));
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const columns: ColumnsType<AudioAsset> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      ellipsis: true,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{text}</Typography.Text>
          {record.description && (
            <Typography.Text 
              type="secondary" 
              style={{ fontSize: '12px' }}
              ellipsis={{ tooltip: record.description }}
            >
              {record.description}
            </Typography.Text>
          )}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color={type === 'music' ? 'blue' : 'green'}>
          {type === 'music' ? 'Music' : 'Sound Effect'}
        </Tag>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => category ? <Tag>{category}</Tag> : '-'
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration) => formatDuration(duration)
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 90,
      render: (size) => formatFileSize(size)
    },
    {
      title: 'AI Status',
      dataIndex: 'aiAnalyzed',
      key: 'aiAnalyzed',
      width: 100,
      render: (analyzed, record) => {
        if (record.status === 'PROCESSING') {
          return <Tag color="processing">Processing</Tag>;
        }
        return analyzed ? <Tag color="success">Analyzed</Tag> : <Tag color="default">Not Analyzed</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={playingAudioId === record.id ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handlePlayPause(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<SyncOutlined />}
            onClick={() => handleAnalyze(record)}
            disabled={record.status === 'PROCESSING'}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={2}>Audio Library</Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setUploadModalVisible(true)}
        >
          Upload Audio
        </Button>
      </div>

      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key as any)}>
        <TabPane tab="All" key="all" />
        <TabPane tab="Music" key="music" />
        <TabPane tab="Sound Effects" key="sound_effect" />
      </Tabs>

      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by name, description, or tags"
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Select
          value={sortBy}
          onChange={setSortBy}
          style={{ width: 150 }}
        >
          <Option value="createdAt_desc">Newest First</Option>
          <Option value="createdAt_asc">Oldest First</Option>
          <Option value="name_asc">Name A-Z</Option>
          <Option value="name_desc">Name Z-A</Option>
          <Option value="duration_asc">Duration (Short)</Option>
          <Option value="duration_desc">Duration (Long)</Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pageIndex + 1,
          pageSize: pageSize,
          total: total,
          onChange: (page, size) => {
            setPageIndex(page - 1);
            setPageSize(size || 20);
          },
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`
        }}
      />

      <UploadAudioModal
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
        onSuccess={() => {
          setUploadModalVisible(false);
          loadData();
        }}
        defaultType={activeTab === 'all' ? undefined : activeTab}
      />

      {selectedAudio && (
        <EditAudioModal
          visible={editModalVisible}
          audio={selectedAudio}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedAudio(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedAudio(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default AudioLibrary;
